import Select from "react-select";

const ACCENT = "var(--secondary-color)";
const ACCENT_SOFT = "rgba(210,171,103,0.12)";

const getStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 8,
    backgroundColor: state.isDisabled ? "#f9fafb" : "white",
    borderColor: hasError ? "#f87171" : state.isFocused ? ACCENT : "#d1d5db",
    boxShadow: state.isFocused
      ? "0 0 0 3px rgba(210,171,103,0.3)"
      : "0 1px 2px rgba(0,0,0,0.05)",
    fontSize: 14,
    cursor: "pointer",
    transition: "border-color 150ms, box-shadow 150ms",
    "&:hover": { borderColor: hasError ? "#f87171" : ACCENT },
  }),
  /* Multi: chips wrap onto rows with even spacing; past ~3 rows the list scrolls instead of growing */
  valueContainer: (base, state) =>
    state.isMulti && state.hasValue
      ? { ...base, padding: "5px 6px", gap: 6, maxHeight: 112, overflowY: "auto" }
      : { ...base, padding: "2px 10px", gap: 4 },
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  /* Keep the clear / chevron buttons on the first row when chips wrap */
  indicatorsContainer: (base) => ({ ...base, alignSelf: "flex-start", height: 40 }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  singleValue: (base) => ({ ...base, color: "#111827" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: "0 10px",
    color: "#9ca3af",
    transition: "transform 200ms",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "none",
    "&:hover": { color: "#6b7280" },
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "0 4px",
    color: "#9ca3af",
    "&:hover": { color: "#dc2626" },
  }),
  multiValue: (base) => ({
    ...base,
    margin: 0,
    minWidth: 0,
    /* Leave room for the search input so a long chip doesn't push it onto an empty row */
    maxWidth: "calc(100% - 14px)",
    alignItems: "center",
    borderRadius: 9999,
    backgroundColor: ACCENT_SOFT,
    boxShadow: "inset 0 0 0 1px rgba(210,171,103,0.3)",
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: 12,
    fontWeight: 500,
    color: "#374151",
    padding: "3px 2px 3px 10px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    borderRadius: 9999,
    margin: "0 3px 0 2px",
    padding: 3,
    color: "#9ca3af",
    ":hover": { backgroundColor: "#fee2e2", color: "#dc2626" },
  }),
  menu: (base) => ({
    ...base,
    marginTop: 6,
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #e5e7eb",
    boxShadow: "0 10px 30px -5px rgba(0,0,0,0.12), 0 4px 10px -6px rgba(0,0,0,0.08)",
  }),
  menuList: (base) => ({ ...base, padding: 6 }),
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    padding: "8px 10px",
    borderRadius: 8,
    cursor: "pointer",
    backgroundColor: state.isSelected ? ACCENT : state.isFocused ? ACCENT_SOFT : "transparent",
    color: state.isSelected ? "white" : "#111827",
    fontWeight: state.isSelected ? 600 : 400,
    ":active": { backgroundColor: state.isSelected ? ACCENT : "rgba(210,171,103,0.2)" },
  }),
  noOptionsMessage: (base) => ({ ...base, fontSize: 13, color: "#9ca3af" }),
});

/* Searchable dropdown styled like the dashboard's form inputs.
   Values are react-select options: { value, label } (an array when isMulti). */
export default function FormSelect({ hasError = false, isMulti = false, ...props }) {
  return (
    <Select
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      styles={getStyles(hasError)}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      noOptionsMessage={() => "Nuk ka opsione"}
      {...props}
    />
  );
}
